import AsyncStorage from '@react-native-async-storage/async-storage';

const COOKIE_CONSENT_KEY = 'cookie_consent_preferences';

// Default cookie consent (false = not consented, true = consented)
const DEFAULT_CONSENT = false;

/**
 * Cookie Service - Manages cookie consent and preferences
 */
class CookieService {
  /**
   * Check if user has already given cookie consent
   * @returns {Promise<boolean>}
   */
  async hasConsentBeenGiven() {
    try {
      const consent = await AsyncStorage.getItem(COOKIE_CONSENT_KEY);
      return consent !== null;
    } catch (error) {
      console.error('Error checking cookie consent:', error);
      return false;
    }
  }

  /**
   * Get user's cookie consent status
   * @returns {Promise<boolean>}
   */
  async getCookieConsent() {
    try {
      const consent = await AsyncStorage.getItem(COOKIE_CONSENT_KEY);
      if (consent !== null) {
        return JSON.parse(consent);
      }
      return DEFAULT_CONSENT;
    } catch (error) {
      console.error('Error getting cookie consent:', error);
      return DEFAULT_CONSENT;
    }
  }

  /**
   * Save user's cookie consent
   * @param {boolean} consent - Cookie consent status
   * @returns {Promise<void>}
   */
  async saveCookieConsent(consent) {
    try {
      await AsyncStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));

      // Apply cookie consent immediately
      this.applyCookieConsent(consent);
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  }

  /**
   * Accept all cookies
   * @returns {Promise<void>}
   */
  async acceptAllCookies() {
    await this.saveCookieConsent(true);
  }

  /**
   * Reject all non-essential cookies
   * @returns {Promise<void>}
   */
  async rejectAllCookies() {
    await this.saveCookieConsent(false);
  }

  /**
   * Check if cookies are allowed
   * @returns {Promise<boolean>}
   */
  async isCookieAllowed() {
    try {
      return await this.getCookieConsent();
    } catch (error) {
      console.error('Error checking cookie consent:', error);
      return false; // Default to not allowed
    }
  }

  /**
   * Get user's cookie preferences (compatible with useCookieConsent hook)
   * @returns {Promise<Object>} Object with essential and analytics properties
   */
  async getCookiePreferences() {
    try {
      const consent = await this.getCookieConsent();
      return {
        essential: true, // Essential cookies are always true
        analytics: consent, // Analytics follows the main consent
      };
    } catch (error) {
      console.error('Error getting cookie preferences:', error);
      return {
        essential: true,
        analytics: false,
      };
    }
  }

  /**
   * Save user's cookie preferences (compatible with useCookieConsent hook)
   * @param {Object} preferences - Object with essential and analytics properties
   * @returns {Promise<void>}
   */
  async saveCookiePreferences(preferences) {
    try {
      // Use the analytics preference as the main consent value
      const consent = preferences.analytics;
      await this.saveCookieConsent(consent);
    } catch (error) {
      console.error('Error saving cookie preferences:', error);
    }
  }

  /**
   * Check if a specific cookie category is allowed
   * @param {string} category - Category to check ('essential' or 'analytics')
   * @returns {Promise<boolean>}
   */
  async isCookieCategoryAllowed(category) {
    try {
      if (category === 'essential') {
        return true; // Essential cookies are always allowed
      }
      if (category === 'analytics') {
        return await this.getCookieConsent();
      }
      return false; // Unknown categories are not allowed
    } catch (error) {
      console.error('Error checking cookie category:', error);
      return category === 'essential'; // Only essential cookies allowed by default
    }
  }

  /**
   * Clear all cookie preferences (for testing or reset purposes)
   * @returns {Promise<void>}
   */
  async clearCookiePreferences() {
    try {
      await AsyncStorage.removeItem(COOKIE_CONSENT_KEY);
    } catch (error) {
      console.error('Error clearing cookie preferences:', error);
    }
  }

  /**
   * Apply cookie consent to the application
   * This is where you would integrate with analytics, marketing tools, etc.
   * @param {boolean} consent - Cookie consent status
   */
  applyCookieConsent(consent) {
    if (consent) {
      this.enableAnalytics();
    } else {
      this.disableAnalytics();
    }

    // Essential cookies are always enabled
    this.enableEssential();
  }

  /**
   * Enable analytics tracking
   */
  enableAnalytics() {
    // Load Clarity analytics if not already loaded
    this.loadClarityAnalytics();
  }

  /**
   * Disable analytics tracking
   */
  disableAnalytics() {
    // Disable Clarity analytics
    this.unloadClarityAnalytics();
  }

  /**
   * Load Clarity analytics dynamically
   */
  loadClarityAnalytics() {
    if (typeof window !== 'undefined' && !window.clarity) {
      try {
        const clarityId = process.env.CLARITY_ID;
        if (clarityId) {
          (function (c, l, a, r, i, t, y) {
            c[a] =
              c[a] ||
              function () {
                (c[a].q = c[a].q || []).push(arguments);
              };
            t = l.createElement(r);
            t.async = 1;
            t.src = 'https://www.clarity.ms/tag/' + i;
            y = l.getElementsByTagName(r)[0];
            y.parentNode.insertBefore(t, y);
          })(window, document, 'clarity', 'script', clarityId);
        }
      } catch (error) {
        console.error('Error loading Clarity analytics:', error);
      }
    }
  }

  /**
   * Unload Clarity analytics
   */
  unloadClarityAnalytics() {
    if (typeof window !== 'undefined' && window.clarity) {
      try {
        // Clear Clarity data and disable tracking
        if (window.clarity && typeof window.clarity === 'function') {
          window.clarity('stop');
        }
      } catch (error) {
        console.error('Error disabling Clarity analytics:', error);
      }
    }
  }

  /**
   * Enable essential features (always enabled)
   */
  enableEssential() {
    // Essential features are always enabled
    // Example: Authentication, basic app functionality, etc.
  }

  /**
   * Initialize cookie service - call this when app starts
   * @returns {Promise<void>}
   */
  async initialize() {
    try {
      const hasConsent = await this.hasConsentBeenGiven();
      if (hasConsent) {
        const consent = await this.getCookieConsent();
        this.applyCookieConsent(consent);
      }
    } catch (error) {
      console.error('Error initializing cookie service:', error);
    }
  }
}

// Export singleton instance
export default new CookieService();
